<template>
  <ApexChart v-if="chartData && chartData.series" element="StarboardFuelCharacteristics" :chartOption="chartData"
    :series="chartData.series" />
</template>

<script>
import moment from 'moment';

export default {
  name: 'StarboardFuelCharacteristics',
  props: ['series'],
  computed: {
    chartData() {
      if (!this.series) return null;
      const isMobile = window.innerWidth <= 991;

      // 1. Ekstraksi data
      const MappedStarboardInFlow = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.STARBOARD_InFlow))
          ? Number(data.fmData.STARBOARD_InFlow) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardRpm = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.STARBOARD && Number.isFinite(Number(data.rpmData.STARBOARD.rpm))
          ? Number(data.rpmData.STARBOARD.rpm) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardDens = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.STARBOARD_InDens))
          ? Number(data.fmData.STARBOARD_InDens) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardInTemp = this.series.map((data) => {
        const raw = data && data.fmData && data.fmData.STARBOARD_IN && Number.isFinite(Number(data.fmData.STARBOARD_IN.temperature))
          ? Number(data.fmData.STARBOARD_IN.temperature) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });

      // 2. Terapkan interpolasi
      const InterpolatedStarboardInFlow = this.interpolateNulls(MappedStarboardInFlow);
      const InterpolatedStarboardRpm = this.interpolateNulls(MappedStarboardRpm);
      const InterpolatedStarboardDens = this.interpolateNulls(MappedStarboardDens);
      const InterpolatedStarboardInTemp = this.interpolateNulls(MappedStarboardInTemp);

      const safeMax = (arr, fallback = 10) => {
        const vals = arr.map((v) => (typeof v === 'string' ? Number(v) : v)).filter((v) => Number.isFinite(v));
        return vals.length ? Math.max(...vals) : fallback;
      };

      let MaxInFlow = safeMax(InterpolatedStarboardInFlow);
      MaxInFlow += (MaxInFlow * 0.10);

      let MaxRpm = safeMax(InterpolatedStarboardRpm, 1000);
      MaxRpm = (Math.ceil(MaxRpm / 100) * 100) + 100;

      let MaxStarboardDens = safeMax(InterpolatedStarboardDens);
      MaxStarboardDens += (MaxStarboardDens * 0.10);

      let MaxStarboardInTemp = safeMax(InterpolatedStarboardInTemp);
      MaxStarboardInTemp += (MaxStarboardInTemp * 0.10);

      const seriesNames = {
        stbdInFlow: 'Starboard In Flow (L/h)',
        stbdRpm: 'Starboard RPM',
        stbdInDens: 'Starboard In Density (g/cm³)',
        stbdInTemp: 'Starboard In Temp (°C)',
      };

      const seriesData = [
        // Starboard In Flow
        { name: seriesNames.stbdInFlow, data: MappedStarboardInFlow, color: '#0084ff' },
        { name: `${seriesNames.stbdInFlow} (Interpolated)`, data: InterpolatedStarboardInFlow, color: 'rgba(0, 132, 255, 0.4)' },
        // Starboard RPM
        { name: seriesNames.stbdRpm, data: MappedStarboardRpm, color: '#884DFF' },
        { name: `${seriesNames.stbdRpm} (Interpolated)`, data: InterpolatedStarboardRpm, color: 'rgba(136, 77, 255, 0.4)' },
        // Starboard In Density
        { name: seriesNames.stbdInDens, data: MappedStarboardDens, color: '#10B981' },
        { name: `${seriesNames.stbdInDens} (Interpolated)`, data: InterpolatedStarboardDens, color: 'rgba(16, 185, 129, 0.4)' },
        // Starboard In Temp
        { name: seriesNames.stbdInTemp, data: MappedStarboardInTemp, color: '#F97316' },
        { name: `${seriesNames.stbdInTemp} (Interpolated)`, data: InterpolatedStarboardInTemp, color: 'rgba(249, 115, 22, 0.4)' },
      ];

      const legendFillColors = seriesData.map((s) => (s.name.includes('(Interpolated)') ? 'transparent' : s.color));

      return {
        series: seriesData,
        chart: {
          height: 600,
          width: '100%',
          type: 'line',
          stacked: false,
          animations: {
            enabled: false,
          },
          events: {
            legendClick(chartContext, seriesIndex) {
              const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
              const interpolatedName = `${seriesName} (Interpolated)`;
              chartContext.toggleSeries(interpolatedName);
            },
          },
          zoom: {
            enabled: true,
            allowMouseWheelZoom: false,
          },
        },
        markers: {
          size: 4,
        },
        stroke: {
          width: [3, 2, 3, 2, 3, 2, 3, 2],
          dashArray: [0, 8, 0, 8, 0, 8, 0, 8],
        },
        legend: {
          position: 'bottom',
          formatter(seriesName) {
            if (seriesName.includes('(Interpolated)')) {
              return '';
            }
            return seriesName;
          },
          markers: {
            fillColors: legendFillColors,
          },
        },
        tooltip: {
          shared: true,
          custom({
            // eslint-disable-next-line no-unused-vars
            series, seriesIndex, dataPointIndex, w,
          }) {
            const header = w.globals.categoryLabels[dataPointIndex];
            let tooltipContent = '';

            for (let i = 0; i < series.length; i++) {
              if (!w.globals.seriesNames[i].includes('(Interpolated)')) {
                const seriesValue = series[i][dataPointIndex];
                if (seriesValue !== null && typeof seriesValue !== 'undefined') {
                  const seriesColor = w.globals.colors[i];
                  const seriesName = w.globals.seriesNames[i];
                  tooltipContent += `
                    <div class="apexcharts-tooltip-series-group" style="order: ${i}; display: flex;">
                      <span class="apexcharts-tooltip-marker" style="background-color: ${seriesColor};"></span>
                      <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                        <div class="apexcharts-tooltip-y-group">
                          <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                          <span class="apexcharts-tooltip-text-y-value">${seriesValue}</span>
                        </div>
                      </div>
                    </div>`;
                }
              }
            }

            if (tooltipContent === '') {
              return '';
            }

            return `
              <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                ${header}
              </div>
              <div class="apexcharts-tooltip-series-group-wrapper" style="display: flex; flex-direction: column;">
                ${tooltipContent}
              </div>`;
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            formatter(val) {
              return moment(val).format('DD-MM-YYYY HH:mm');
            },
          },
          categories: this.series.map((data) => moment(data.timestamp * 1000).format('DD MMMM YYYY HH:mm')),
          tickAmount: isMobile ? 3 : undefined,
        },
        yaxis: [
          // Starboard Flow Axis
          {
            max: MaxInFlow,
            title: { text: 'Stbd. In Flow (L/h)' },
            show: !isMobile,
          },
          { max: MaxInFlow, show: false },
          // Starboard RPM Axis
          {
            opposite: true,
            max: MaxRpm,
            title: { text: 'Stbd. RPM' },
            show: !isMobile,
          },
          { opposite: true, max: MaxRpm, show: false },
          // Starboard Density Axis
          {
            opposite: true,
            max: MaxStarboardDens,
            title: { text: 'Stbd. In Density (g/cm³)' },
            show: !isMobile,
          },
          { opposite: true, max: MaxStarboardDens, show: false },
          // Starboard Temp Axis
          {
            opposite: true,
            max: MaxStarboardInTemp,
            title: { text: 'Stbd. In Temperature (°C)' },
            show: !isMobile,
          },
          { opposite: true, max: MaxStarboardInTemp, show: false },
        ],
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }

            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>

<style scoped></style>
